import { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { X, RotateCcw, ArrowLeftRight, History, Clock, User, Bot } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getVersions } from '../lib/assessment-versions';
import type { AssessmentVersion, AssessmentType } from '../lib/assessment-versions';
import { ConfirmDialog } from './ConfirmDialog';

export interface VersionHistoryDrawerProps {
  assessmentId: string | undefined;
  assessmentType: AssessmentType;
  isOpen: boolean;
  onClose: () => void;
  onRestore: (snapshot: Record<string, unknown>, fromVersionNumber: number) => Promise<void>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const SCORE_COLORS: Record<number, string> = {
  1: 'bg-red-100 text-red-700',
  2: 'bg-orange-100 text-orange-700',
  3: 'bg-yellow-100 text-yellow-700',
  4: 'bg-blue-100 text-blue-700',
  5: 'bg-green-100 text-green-700',
};

function ScoreBadge({ score }: { score: number }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${SCORE_COLORS[score] ?? 'bg-gray-100 text-gray-600'}`}>
      {score}/5
    </span>
  );
}

function truncate(text: string, max = 200): string {
  return text && text.length > max ? text.slice(0, max) + '…' : text;
}

const FIELD_LABELS: Record<string, string> = {
  score: 'Score',
  analysis: 'Analysis',
  goals: 'Goals / Action Plan',
  evidence: 'Evidence',
  part_a: 'Part A — Session Identification',
  part_b: 'Part B — Debriefing Framework',
  part_c: 'Part C — Supporting Material',
  part_d: 'Part D — Instructor Notes',
  status: 'Status',
  invocation_line: 'Invocation Line',
};

function extractFields(
  snapshot: Record<string, unknown>,
  assessmentType: AssessmentType,
): Record<string, unknown> {
  if (assessmentType === 'pilot' || assessmentType === 'instructor') {
    return {
      score: snapshot.score,
      analysis: snapshot.analysis,
      goals: snapshot.goals,
      evidence: snapshot.evidence,
    };
  }
  return {
    part_a: snapshot.part_a,
    part_b: snapshot.part_b,
    part_c: snapshot.part_c,
    part_d: snapshot.part_d,
    invocation_line: snapshot.invocation_line,
  };
}

interface DiffEntry {
  field: string;
  label: string;
  oldVal: unknown;
  newVal: unknown;
}

function normalizeForDiff(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeForDiff);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
      a.localeCompare(b),
    );
    const normalized: Record<string, unknown> = {};
    entries.forEach(([key, child]) => {
      normalized[key] = normalizeForDiff(child);
    });
    return normalized;
  }
  return value;
}

function valuesAreEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(normalizeForDiff(a)) === JSON.stringify(normalizeForDiff(b));
}

function getDiffs(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  assessmentType: AssessmentType,
): DiffEntry[] {
  const fieldsA = extractFields(a, assessmentType);
  const fieldsB = extractFields(b, assessmentType);
  const result: DiffEntry[] = [];
  for (const field of Object.keys(fieldsA)) {
    if (!valuesAreEqual(fieldsA[field], fieldsB[field])) {
      result.push({
        field,
        label: FIELD_LABELS[field] ?? field,
        oldVal: fieldsA[field],
        newVal: fieldsB[field],
      });
    }
  }
  return result;
}

function renderFieldValue(val: unknown, field: string): React.ReactNode {
  if (val === undefined || val === null) return <span className="text-gray-400 italic">—</span>;
  if (field === 'score' && typeof val === 'number') return <ScoreBadge score={val} />;
  if (typeof val === 'string') {
    return <span className="text-sm text-gray-700 whitespace-pre-wrap">{truncate(val, 180)}</span>;
  }
  if (field === 'evidence' && Array.isArray(val)) {
    const items = val as Array<{ quote?: string }>;
    return (
      <div>
        <span className="font-semibold text-sm">{items.length} item{items.length !== 1 ? 's' : ''}</span>
        {items.slice(0, 2).map((ev, i) => (
          <p key={i} className="text-xs text-gray-600 mt-1 truncate">
            &ldquo;{truncate(ev.quote ?? '', 80)}&rdquo;
          </p>
        ))}
        {items.length > 2 && <p className="text-xs text-gray-400 mt-0.5">+{items.length - 2} more</p>}
      </div>
    );
  }
  return <span className="text-sm text-gray-500 italic">Modified</span>;
}

const TEXT_DIFF_FIELDS = new Set([
  'analysis',
  'goals',
  'part_a',
  'part_b',
  'part_c',
  'part_d',
  'invocation_line',
  'evidence'
]);

function toDiffText(val: unknown, field: string): string {
  if (val === undefined || val === null) return '—';
  if (typeof val === 'string') return val;
  if (field === 'evidence' && Array.isArray(val)) {
    return (val as Array<{ quote?: string }>)
      .map((item) => item.quote?.trim())
      .filter(Boolean)
      .join('\n');
  }
  if (typeof val === 'object') {
    return JSON.stringify(val, null, 2);
  }
  return String(val);
}

function getInlineDiff(oldText: string, newText: string) {
  let prefixLength = 0;
  const maxPrefix = Math.min(oldText.length, newText.length);
  while (prefixLength < maxPrefix && oldText[prefixLength] === newText[prefixLength]) {
    prefixLength += 1;
  }

  let oldSuffixLength = oldText.length - 1;
  let newSuffixLength = newText.length - 1;
  while (
    oldSuffixLength >= prefixLength &&
    newSuffixLength >= prefixLength &&
    oldText[oldSuffixLength] === newText[newSuffixLength]
  ) {
    oldSuffixLength -= 1;
    newSuffixLength -= 1;
  }

  return {
    prefix: oldText.slice(0, prefixLength),
    oldChanged: oldText.slice(prefixLength, oldSuffixLength + 1),
    newChanged: newText.slice(prefixLength, newSuffixLength + 1),
    suffix: oldText.slice(oldSuffixLength + 1),
  };
}

function InlineTextDiff({
  oldText,
  newText,
}: {
  oldText: string;
  newText: string;
}) {
  const diff = getInlineDiff(oldText, newText);

  return (
    <div className="grid grid-cols-2 divide-x divide-gray-200">
      <div className="p-3 bg-red-50 min-h-[48px]">
        <div className="text-xs text-red-400 mb-1 font-medium">Version A</div>
        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
          <span>{diff.prefix}</span>
          {diff.oldChanged && <span className="bg-red-200 rounded px-0.5">{diff.oldChanged}</span>}
          <span>{diff.suffix}</span>
        </p>
      </div>
      <div className="p-3 bg-green-50 min-h-[48px]">
        <div className="text-xs text-green-500 mb-1 font-medium">Version B</div>
        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
          <span>{diff.prefix}</span>
          {diff.newChanged && <span className="bg-green-200 rounded px-0.5">{diff.newChanged}</span>}
          <span>{diff.suffix}</span>
        </p>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function DiffRow({
  field,
  label,
  oldVal,
  newVal,
}: {
  field: string;
  label: string;
  oldVal: unknown;
  newVal: unknown;
}) {
  const shouldShowInlineTextDiff = TEXT_DIFF_FIELDS.has(field);
  const oldText = shouldShowInlineTextDiff ? toDiffText(oldVal, field) : '';
  const newText = shouldShowInlineTextDiff ? toDiffText(newVal, field) : '';

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
      <div className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        {label}
      </div>
      {shouldShowInlineTextDiff ? (
        <InlineTextDiff oldText={oldText} newText={newText} />
      ) : (
        <div className="grid grid-cols-2 divide-x divide-gray-200">
          <div className="p-3 bg-red-50 min-h-[48px]">
            <div className="text-xs text-red-400 mb-1 font-medium">Version A</div>
            {renderFieldValue(oldVal, field)}
          </div>
          <div className="p-3 bg-green-50 min-h-[48px]">
            <div className="text-xs text-green-500 mb-1 font-medium">Version B</div>
            {renderFieldValue(newVal, field)}
          </div>
        </div>
      )}
    </div>
  );
}

function VersionRow({
  version,
  authorLabel,
  isRestoring,
  onRestore,
}: {
  version: AssessmentVersion;
  authorLabel: string;
  isRestoring: boolean;
  onRestore: () => void;
}) {
  return (
    <div
      className={`border rounded-lg p-4 ${
        version.is_ai_baseline
          ? 'border-blue-200 bg-blue-50'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-navy text-sm">Version {version.version_number}</span>
            {version.is_ai_baseline && (
              <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                <Bot className="w-3 h-3" /> AI Generated
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
            <User className="w-3 h-3 shrink-0" />
            <span className="truncate">{authorLabel}</span>
            <span className="text-gray-300">•</span>
            <span className="shrink-0">{format(new Date(version.created_at), 'PP p')}</span>
          </div>
          {version.change_summary && (
            <p className="mt-1.5 text-xs text-gray-600 italic">{version.change_summary}</p>
          )}
        </div>
        {!version.is_ai_baseline && (
          <button
            onClick={onRestore}
            disabled={isRestoring}
            className="shrink-0 flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-navy border border-gray-200 hover:border-navy px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-3 h-3" />
            {isRestoring ? 'Restoring…' : 'Restore'}
          </button>
        )}
      </div>
    </div>
  );
}

function VersionList({
  versions,
  getAuthorLabel,
  restoringVersion,
  onRestore,
}: {
  versions: AssessmentVersion[];
  getAuthorLabel: (v: AssessmentVersion) => string;
  restoringVersion: number | null;
  onRestore: (v: AssessmentVersion) => void;
}) {
  return (
    <div className="p-4 space-y-2">
      {versions.map((v) => (
        <VersionRow
          key={v.id}
          version={v}
          authorLabel={getAuthorLabel(v)}
          isRestoring={restoringVersion === v.version_number}
          onRestore={() => onRestore(v)}
        />
      ))}
    </div>
  );
}

function DiffPane({
  versions,
  assessmentType,
  compareLeft,
  compareRight,
  onChangeLeft,
  onChangeRight,
  getAuthorLabel,
}: {
  versions: AssessmentVersion[];
  assessmentType: AssessmentType;
  compareLeft: number | null;
  compareRight: number | null;
  onChangeLeft: (n: number) => void;
  onChangeRight: (n: number) => void;
  getAuthorLabel: (v: AssessmentVersion) => string;
}) {
  const versionA = useMemo(
    () => versions.find((v) => v.version_number === compareLeft),
    [versions, compareLeft],
  );
  const versionB = useMemo(
    () => versions.find((v) => v.version_number === compareRight),
    [versions, compareRight],
  );
  const diffs = useMemo(
    () =>
      versionA && versionB && compareLeft !== compareRight
        ? getDiffs(versionA.snapshot, versionB.snapshot, assessmentType)
        : [],
    [versionA, versionB, compareLeft, compareRight, assessmentType],
  );

  const versionLabel = (v: AssessmentVersion) =>
    `v${v.version_number} — ${v.is_ai_baseline ? 'AI Generated' : getAuthorLabel(v)}`;

  return (
    <div className="p-4">
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Version A</label>
          <select
            value={compareLeft ?? ''}
            onChange={(e) => onChangeLeft(Number(e.target.value))}
            className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-navy"
          >
            {versions.map((v) => (
              <option key={v.version_number} value={v.version_number}>
                {versionLabel(v)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Version B</label>
          <select
            value={compareRight ?? ''}
            onChange={(e) => onChangeRight(Number(e.target.value))}
            className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-navy"
          >
            {versions.map((v) => (
              <option key={v.version_number} value={v.version_number}>
                {versionLabel(v)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!versionA || !versionB ? (
        <p className="text-sm text-gray-400 text-center py-10">Select two versions to compare</p>
      ) : compareLeft === compareRight ? (
        <p className="text-sm text-gray-400 text-center py-10">Select two different versions</p>
      ) : diffs.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-sm text-gray-500 font-medium">No differences</p>
          <p className="text-xs text-gray-400 mt-1">These versions are identical.</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-3">
            {diffs.length} field{diffs.length !== 1 ? 's' : ''} changed
          </p>
          {diffs.map((d) => (
            <DiffRow field={d.field} key={d.field} label={d.label} oldVal={d.oldVal} newVal={d.newVal} />
          ))}
        </>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function VersionHistoryDrawer({
  assessmentId,
  assessmentType,
  isOpen,
  onClose,
  onRestore,
}: VersionHistoryDrawerProps) {
  const [versions, setVersions] = useState<AssessmentVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<'list' | 'diff'>('list');
  const [compareLeft, setCompareLeft] = useState<number | null>(null);
  const [compareRight, setCompareRight] = useState<number | null>(null);
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null);
  const [pendingRestoreVersion, setPendingRestoreVersion] = useState<AssessmentVersion | null>(null);

  const loadVersions = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const data = await getVersions(assessmentId, assessmentType);
      setVersions(data);

      const userIds = [...new Set(data.map((v) => v.created_by).filter(Boolean))] as string[];
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, full_name')
          .in('id', userIds);
        if (users) {
          const nameMap: Record<string, string> = {};
          (users as { id: string; full_name: string }[]).forEach((u) => {
            nameMap[u.id] = u.full_name;
          });
          setUserNames(nameMap);
        }
      }

      // Default compare: AI baseline vs latest
      if (data.length >= 2) {
        const baseline = data.find((v) => v.is_ai_baseline);
        const latest = data[0]; // DESC order
        const oldest = data[data.length - 1];
        if (latest && oldest) {
          setCompareLeft(baseline?.version_number ?? oldest.version_number);
          setCompareRight(latest.version_number);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [assessmentId, assessmentType]);

  useEffect(() => {
    if (isOpen) {
      setMode('list');
      loadVersions();
    }
  }, [isOpen, loadVersions]);

  const handleRestoreRequest = useCallback((version: AssessmentVersion) => {
    setPendingRestoreVersion(version);
  }, []);

  const handleConfirmRestore = useCallback(async () => {
    if (!pendingRestoreVersion) return;

    setRestoringVersion(pendingRestoreVersion.version_number);
    try {
      await onRestore(pendingRestoreVersion.snapshot, pendingRestoreVersion.version_number);
      await loadVersions();
      setPendingRestoreVersion(null);
    } finally {
      setRestoringVersion(null);
    }
  }, [pendingRestoreVersion, onRestore, loadVersions]);

  const getAuthorLabel = useCallback(
    (v: AssessmentVersion) => {
      if (v.is_ai_baseline) return 'AI Generated';
      if (v.created_by) {
        const authorName = userNames[v.created_by];
        if (authorName) return authorName;
      }
      return 'Instructor';
    },
    [userNames],
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Drawer panel */}
      <div className="fixed top-0 right-0 bottom-0 w-[480px] bg-white z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-navy text-white shrink-0">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5" />
            <h2 className="font-semibold text-lg text-white">Version History</h2>
            {versions.length > 0 && (
              <span className="text-xs bg-white/20 rounded-full px-2 py-0.5">
                {versions.length}
              </span>
            )}
          </div>
          <button onClick={onClose} className="hover:text-gray-300 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs — only shown when there are ≥2 versions */}
        {versions.length >= 2 && (
          <div className="flex border-b border-gray-200 shrink-0">
            <button
              onClick={() => setMode('list')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                mode === 'list'
                  ? 'text-navy border-b-2 border-navy bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All Versions
            </button>
            <button
              onClick={() => setMode('diff')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                mode === 'diff'
                  ? 'text-navy border-b-2 border-navy bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
              Compare
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
            </div>
          ) : versions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-56 text-center px-8">
              <Clock className="w-10 h-10 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No version history available</p>
              <p className="text-gray-400 text-sm mt-1">
                This assessment was created before version tracking was enabled.
              </p>
            </div>
          ) : mode === 'list' ? (
            <VersionList
              versions={versions}
              getAuthorLabel={getAuthorLabel}
              restoringVersion={restoringVersion}
              onRestore={handleRestoreRequest}
            />
          ) : (
            <DiffPane
              versions={versions}
              assessmentType={assessmentType}
              compareLeft={compareLeft}
              compareRight={compareRight}
              onChangeLeft={setCompareLeft}
              onChangeRight={setCompareRight}
              getAuthorLabel={getAuthorLabel}
            />
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={pendingRestoreVersion !== null}
        title="Restore Version"
        description={
          pendingRestoreVersion
            ? `Restore to version ${pendingRestoreVersion.version_number}? The current state will be saved as a new version first.`
            : ''
        }
        confirmLabel="Restore version"
        cancelLabel="Keep current"
        isConfirming={
          pendingRestoreVersion !== null && restoringVersion === pendingRestoreVersion.version_number
        }
        onConfirm={handleConfirmRestore}
        onCancel={() => setPendingRestoreVersion(null)}
      />
    </>
  );
}
